/**
 * `board_relation` (Connect boards) — chips for the linked items, and monday's item picker.
 *
 * Captured this round: the **Connected board column settings** dialog
 * (`editors/board_relation/dialog-settings.png`) is
 * *"This column is connected to one board"* + a row per board with **Disconnect**, then
 * *Connection settings*: **Allow linking to multiple items** (a toggle) and **Turn on two-way
 * connection** (a button), an access banner, and a footer of *Add a mirror column* +
 * **+ Connect more boards**. That is the settings dialog this folder ships.
 *
 * The cell's own editor is monday's "All items" search list (`sub2-search`), 400 px wide.
 *
 * Round 9s / P-046: the **cell** renders the linked items' names, resolved by `linked-names.ts`
 * (see that file for the permission rule). It used to render the stored ids as `#1782545680`,
 * which is what the sweep photographed on the real `Deals` board.
 */
import {
  getColumnType,
  linkedItemIds,
  type BoardRelationSettings,
  type BoardRelationValue,
} from '@elizmonday/shared';
import { CellPopover, PickerSearch, useEditorDraft } from '../primitives';
import { BoardRelationSettingsDialog } from './SettingsDialog';
import { useLinkedItemNames } from './linked-names';
import { defineColumnUi } from '../types';

const shared = getColumnType('board_relation');

/** Measured: monday's connect popover is 400 px wide (`editors/board_relation`). */
export const RELATION_PICKER_WIDTH = 400;

export const boardRelationColumnUi = defineColumnUi<BoardRelationValue, BoardRelationSettings>({
  type: 'board_relation',
  icon: 'link',
  gallery: { group: 'advanced', keywords: ['connect boards', 'relation', 'link items', 'reference'] },
  /** P-022: the registry default. ASSUMED — no `width: null` column of this type is measured. */
  defaultWidth: 140,
  align: 'left',
  groupable: false,

  /**
   * P-046. The chips carry the linked items' **names**, which is what monday's `display_value`
   * is (`specs/M2-tier2-column-types.md` §5.17) — never the stored id. `useLinkedItemNames`
   * batches one `items.byIds` per screenful and answers `null` for a link the viewer may not
   * read, and a `null` contributes nothing to the rendering exactly as it contributes nothing
   * to monday's joined `display_value`: no name is invented, none is leaked, and no bare id is
   * ever painted. A cell whose links are all unreadable therefore renders as empty, the same
   * `display_value: ""` the api answers — recorded as an open question on P-046, because what
   * monday paints in that case could not be measured on this account.
   */
  Cell: ({ value }) => {
    const ids = linkedItemIds(value);
    const nameOf = useLinkedItemNames(ids);
    const named = ids.map((id) => ({ id, name: nameOf(id) })).filter((entry) => typeof entry.name === 'string');
    if (named.length === 0) return <span className="ez-cell__empty" aria-hidden="true" />;
    return (
      <span className="ez-chips" data-align="left">
        {named.slice(0, 2).map((entry) => (
          <span key={entry.id} className="ez-chip ez-chip--relation" title={entry.name as string}>
            {entry.name}
          </span>
        ))}
        {named.length > 2 ? <span className="ez-chip ez-chip--more">{`+${named.length - 2}`}</span> : null}
      </span>
    );
  },

  /**
   * The picker searches the connected board's items. This build has no cross-board item search
   * endpoint yet, so the editor accepts **item ids** — which is exactly what the value stores
   * and what `connect_boards` writes `[09§A.7]` — and says so. Wiring the search is q342.
   */
  /** **P-282**: the id list follows the store while untouched; an untouched dismiss writes
   * nothing (`useEditorDraft`). */
  Editor: ({ value, settings, onCommit, onCancel }) => {
    const editor = useEditorDraft(linkedItemIds(value).join(', '));
    const draft = editor.draft;
    const commit = (): void => {
      const ids = draft
        .split(',')
        .map((entry) => entry.trim())
        .filter((entry) => /^\d+$/.test(entry))
        .map(Number);
      if (ids.length === 0) {
        onCommit(null);
        return;
      }
      const limited = settings.allowMultipleItems === false ? ids.slice(0, 1) : ids;
      onCommit({ linkedPulseIds: limited.map((id) => ({ linkedPulseId: id })) });
    };
    return (
      <CellPopover
        label="Connect items"
        width={RELATION_PICKER_WIDTH}
        onDismiss={() => editor.dismiss(commit, onCancel)}
        onCancel={onCancel}
      >
        <PickerSearch value={draft} onChange={editor.set} placeholder="Item ids, comma separated" />
        <p className="ez-two-field__hint">
          {settings.boardIds?.length
            ? `Connected to board ${settings.boardIds.map(String).join(', ')}`
            : 'This column is not connected to a board yet'}
        </p>
      </CellPopover>
    );
  },

  SettingsDialog: BoardRelationSettingsDialog,

  fromClipboard(text, settings) {
    const ids = text
      .split(',')
      .map((entry) => entry.trim())
      .filter((entry) => /^\d+$/.test(entry));
    return ids.length === 0 ? null : (shared.parseValue({ item_ids: ids }, settings) as BoardRelationValue | null);
  },
  toClipboard(value) {
    return linkedItemIds(value).join(', ');
  },
  bucketsOf(value) {
    return linkedItemIds(value as BoardRelationValue | null).map((id) => ({ key: id, label: `#${id}` }));
  },
});
